import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import { IGNORE_REASON_LABEL } from '@masamune/shared';
import { payablesQuery, daysFromToday, plurale, isSuperAdmin, type PayableRow, type PayablePayment } from './adminApi';
import { useAdminActions } from './useAdminActions';
import { SettlePopover } from './SettlePopover';
import { TrashPopover } from './TrashPopover';

/**
 * Fornitori — fatture ricevute aperte raggruppate per fornitore.
 *   riga gruppo → fornitore · n. documenti · più vecchia · totale · badge Scaduto
 *   click       → espande i documenti (numero, data, scadenza, importo, Paga)
 *
 * Sotto, staccata, la sezione "A rate": le fatture FINANZIATA (le auto). Il
 * fornitore è già stato pagato dalla finanziaria e noi versiamo il canone
 * mensile, che sta fra le uscite ricorrenti: se stessero nella lista di sopra
 * lo stesso debito verrebbe contato due volte. Totale a parte, nessun "Paga".
 */
interface Group { vendor: string; docs: PayableRow[]; total: number; oldest: string | null; overdue: number }

export function FornitoriTab({ canWrite, showHistory }: { canWrite: boolean; showHistory?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const superAdmin = isSuperAdmin(user);
  const openModal = useModalStore((s) => s.open);
  const q = useQuery(payablesQuery({ status: 'aperte', includeHistory: showHistory }));
  const financedQ = useQuery(payablesQuery({ status: 'finanziate' }));
  const trashQ = useQuery(payablesQuery({ status: 'cestinate' }));
  const [openVendor, setOpenVendor] = useState<string | null>(null);

  const financed = useMemo(
    () => (financedQ.data ?? []).filter((p) => superAdmin || p.entity !== 'PERSONALE_ANDREA'),
    [financedQ.data, superAdmin],
  );
  const financedTotal = financed.reduce((a, p) => a + residualOf(p), 0);

  const groups = useMemo<Group[]>(() => {
    const open = (q.data ?? []).filter((p) => !['PAGATA', 'ANNULLATA', 'FINANZIATA'].includes(String(p.status).toUpperCase()))
      .filter((p) => superAdmin || p.entity !== 'PERSONALE_ANDREA');
    const m = new Map<string, Group>();
    for (const p of open) {
      const key = p.vendorName || '—';
      const g = m.get(key) ?? { vendor: key, docs: [], total: 0, oldest: null, overdue: 0 };
      g.docs.push(p);
      g.total += residualOf(p);
      if (p.dueDate && (!g.oldest || p.dueDate < g.oldest)) g.oldest = p.dueDate;
      // Scaduto = le rate aperte con scadenza passata (se l'API non dà le rate, la scadenza del documento)
      const openPays = openPaymentsOf(p);
      if (openPays.length) {
        for (const x of openPays) { const d = daysFromToday(x.dueDate); if (d != null && d < 0) g.overdue += x.amount; }
      } else {
        const d = daysFromToday(p.dueDate);
        if (d != null && d < 0) g.overdue += residualOf(p);
      }
      m.set(key, g);
    }
    // Chi ha lo scaduto più alto prima, poi per totale
    return [...m.values()].sort((a, b) => (b.overdue - a.overdue) || (b.total - a.total));
  }, [q.data, superAdmin]);

  const total = groups.reduce((a, g) => a + g.total, 0);
  const overdueTotal = groups.reduce((a, g) => a + g.overdue, 0);

  if (q.isLoading && !q.data) {
    return <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={5} cols={5} withAvatar={false} /></tbody></table></div>;
  }

  return (
    <>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {/* Se scaduto e totale coincidono si dice a parole: ripetere la stessa cifra
            due volte nella stessa riga fa solo dubitare che siano due cose diverse. */}
        <span className="text-[13px]">
          <strong>{groups.length}</strong> {groups.length === 1 ? 'fornitore' : 'fornitori'} da pagare · <span className="mono">€ {fmtEur(total)}</span>
          {overdueTotal > 0 && (
            <span className="text-alert">
              {Math.round(overdueTotal) === Math.round(total) ? ' · tutti già scaduti' : ` · scaduto € ${fmtEur(overdueTotal)}`}
            </span>
          )}
        </span>
        <div className="spacer" />
        {/* In cima non va niente da toccare (mobile.md §4): sul telefono lo
            stesso bottone sta in fondo, dove arriva il pollice. */}
        {canWrite && (
          <button className="btn btn-sm d-only" onClick={() => openModal('newPayable')}>
            <Icon name="receipt" size={10} /> Nuova fattura ricevuta
          </button>
        )}
      </div>

      {groups.length === 0 ? (
        <EmptyState brand title="Nessun fornitore da pagare" description="Le fatture ricevute aperte, da FattureInCloud o inserite a mano, compaiono qui." />
      ) : (
        <div className="ed-section" data-section="da-pagare">
          {/* Sul telefono ogni fornitore diventa una scheda (mobile.md §3): qui
              la riga si apre davvero — mostra i suoi documenti — quindi il
              chevron della scheda dice il vero e non serve `tbl--static`. */}
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr>
                <th>Fornitore</th>
                {/* "Documenti" è un conteggio, non un'etichetta: sta a destra
                    come tutti gli altri numeri dell'app (è quello che fa la
                    colonna "Fatture" in /suppliers). Restava a sinistra, e
                    accanto a "Da pagare" allineata a destra si vedeva. */}
                <th className="num" style={{ width: 110 }}>Documenti</th>
                <th style={{ width: 150 }}>Più vecchia</th>
                <th className="num" style={{ width: 140 }}>Da pagare</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const open = openVendor === g.vendor;
                const d = daysFromToday(g.oldest);
                const hasOverdue = g.overdue > 0;
                return [
                  <tr key={g.vendor} onClick={() => setOpenVendor(open ? null : g.vendor)} aria-expanded={open}>
                    <td data-m="hd">
                      <div className="font-semibold flex items-center gap-2">
                        {g.vendor}
                        {hasOverdue && <Chip tone="alert" dot>Scaduto € {fmtEur(g.overdue)}</Chip>}
                        {g.docs.some((p) => p.entity === 'PERSONALE_ANDREA') && <Chip tone="ghost">Personale</Chip>}
                      </div>
                    </td>
                    <td data-m="key" data-l="Documenti" className="num mono mute">{g.docs.length}</td>
                    <td data-m="key" data-l="Più vecchia" className={`mono text-[13px] ${d != null && d < 0 ? 'text-alert' : 'mute'}`}>{fmtDate(g.oldest)}</td>
                    <td data-m="key" data-l="Da pagare" className="num mono font-semibold">€ {fmtEur(g.total, true)}</td>
                    {/* Sul telefono la freccia la disegna la scheda da sola (il
                        chevron di .tbl--card): questa cella sarebbe la seconda. */}
                    <td data-m="off"><div className="flex justify-end"><Icon name={open ? 'chevronU' : 'chevronD'} size={12} className="mute" /></div></td>
                  </tr>,
                  open && (
                    <tr key={`${g.vendor}-docs`} className="adm-subrow" style={{ cursor: 'default' }}>
                      <td colSpan={5} style={{ padding: 0 }}>
                        <div className="adm-sublist">
                          {g.docs.map((p) => <DocLine key={p.id} p={p} canWrite={canWrite} />)}
                          {!canWrite && <div className="text-[12px] mute-2 pt-1">Solo lettura</div>}
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {financed.length > 0 && <FinancedSection rows={financed} total={financedTotal} />}
      {(trashQ.data?.length ?? 0) > 0 && <TrashSection rows={trashQ.data ?? []} canWrite={canWrite} />}

      {canWrite && (
        <DesktopOnly
          cosa="La registrazione di una nuova fattura ricevuta"
          perche="richiede anagrafica, scadenze e dati contabili; qui puoi comunque pagare una voce già registrata."
        >
          <div className="m-actionbar m-only">
            <button className="btn btn-primary" onClick={() => openModal('newPayable')}>
              <Icon name="receipt" size={12} /> Nuova fattura ricevuta
            </button>
          </div>
        </DesktopOnly>
      )}
    </>
  );
}

/**
 * Cestino — le fatture ricevute tolte dal gestionale, con il motivo e il tasto
 * per rimetterle a posto. Compare solo se c'è qualcosa dentro: un cestino vuoto
 * in fondo alla pagina è solo rumore.
 */
function TrashSection({ rows, canWrite }: { rows: PayableRow[]; canWrite: boolean }) {
  const { restoreDocs } = useAdminActions();
  return (
    <div className="ed-section" data-section="cestinate">
      <div className="data-hd">
        <span className="t-micro">Cestinate</span>
        <span className="mono mute text-[12px]">{plurale(rows.length, 'documento', 'documenti')}</span>
      </div>
      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>Tolte dal gestionale, non annullate: su FattureInCloud ci sono ancora. Si possono rimettere a posto quando serve.</span>
      </div>
      <div className="adm-sublist">
        {rows.map((p) => (
          <div key={p.id} className="adm-line" style={{ opacity: 0.6 }}>
            <div className="mono text-[12px] mute" style={{ width: 84 }}>{fmtDate(p.issueDate)}</div>
            <div className="min-w-0 flex-1 truncate">
              <span className="font-semibold">{p.vendorName}</span>
              <span className="mute"> · {p.number ?? 's/n'}</span>
            </div>
            <div className="mute text-[12px] truncate" style={{ width: 290 }}>
              {p.ignoredReason ? IGNORE_REASON_LABEL[p.ignoredReason] : '—'}
              {p.ignoredNote ? ` · ${p.ignoredNote}` : ''}
            </div>
            <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(p.total, true)}</div>
            <div style={{ width: 110 }} className="flex justify-end">
              {canWrite && (
                <button className="btn btn-sm btn-ghost" disabled={restoreDocs.isPending}
                  onClick={() => restoreDocs.mutate({ kind: 'payable', ids: [p.id], label: `${p.vendorName} · ${p.number ?? 's/n'}` })}>
                  <Icon name="undo" size={10} /> Ripristina
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * "A rate" — le fatture pagate dalla finanziaria. Righe smorzate e senza pulsante
 * Paga: qui non c'è niente da saldare, la rata mensile è già fra le uscite
 * ricorrenti. Il totale sta per conto suo e non si somma ai fornitori di sopra.
 */
function FinancedSection({ rows, total }: { rows: PayableRow[]; total: number }) {
  return (
    <div className="ed-section" data-section="finanziate">
      <div className="data-hd">
        <span className="t-micro">A rate (già pagate dalla finanziaria)</span>
        {/* La cifra sta insieme al suo simbolo: sul telefono l'etichetta va a
            capo, e andava a capo proprio fra "€" e "107.680". */}
        <span className="mono mute text-[12px]">da ammortizzare <span className="whitespace-nowrap">€ {fmtEur(total)}</span></span>
      </div>
      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>
          Queste il fornitore le ha già incassate: noi paghiamo la rata mensile, che trovi fra le uscite ricorrenti.
          Per questo restano fuori dal conto dei fornitori da pagare.
        </span>
      </div>
      <div className="adm-sublist">
        {rows.map((p) => (
          <div key={p.id} className="adm-line" style={{ opacity: 0.6 }}>
            <div className="mono text-[12px] mute" style={{ width: 84 }}>{fmtDate(p.issueDate)}</div>
            <div className="min-w-0 flex-1 truncate">
              <span className="font-semibold">{p.vendorName}</span>
              <span className="mute"> · {p.number ?? 's/n'}</span>
              <Chip tone="ghost">A rate</Chip>
            </div>
            <div className="mute text-[12px] truncate" style={{ width: 290 }}>
              {p.recurringExpenseName
                ? `la paga "${p.recurringExpenseName}"${p.recurringExpenseAmount ? ` · € ${fmtEur(p.recurringExpenseAmount)} al mese` : ''}`
                : 'rata ricorrente da collegare'}
            </div>
            <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(residualOf(p), true)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Da pagare di un documento: openAmount dell'API, altrimenti il totale. */
function residualOf(p: PayableRow): number {
  return Number(p.openAmount ?? p.total) || 0;
}

/** Rate aperte del documento (POST /uscite/pay vuole l'id della rata, non del documento). */
function openPaymentsOf(p: PayableRow): PayablePayment[] {
  return (p.payments ?? []).filter((x) => x.status === 'DA_PAGARE').slice().sort((a, b) => a.seq - b.seq);
}

/**
 * Un documento = una riga; con più rate aperte, una riga per rata (così "Paga"
 * salda la rata giusta). Il pulsante paga la rata; senza rate note, il documento.
 */
function DocLine({ p, canWrite }: { p: PayableRow; canWrite: boolean }) {
  const openPays = openPaymentsOf(p);
  const partial = String(p.status).toUpperCase() === 'PARZIALE';
  if (openPays.length > 1) {
    return (
      <>
        {openPays.map((x) => (
          <PayLine key={x.id} p={p} paymentId={x.id} amount={x.amount} dueDate={x.dueDate} tag={`rata ${x.seq}`} />
        ))}
      </>
    );
  }
  const only = openPays[0];
  return <PayLine p={p} paymentId={only?.id ?? p.id} amount={only?.amount ?? residualOf(p)} dueDate={only?.dueDate ?? p.dueDate} tag={partial ? 'pagata in parte' : null} />;
}

function PayLine({ p, paymentId, amount, dueDate, tag }: { p: PayableRow; paymentId: string; amount: number; dueDate: string | null; tag: string | null }) {
  const { pay, ignoreDocs } = useAdminActions();
  const [open, setOpen] = useState(false);
  const [trash, setTrash] = useState(false);
  const days = daysFromToday(dueDate);
  const overdue = days != null && days < 0;
  const label = `${p.vendorName} · ${p.number ?? 's/n'}`;
  return (
    <div className="adm-line">
      <div className="mono text-[12px] mute" style={{ width: 84 }}>{fmtDate(p.issueDate)}</div>
      <div className="min-w-0 flex-1 truncate">
        <span className="mono font-semibold">{p.number ?? 's/n'}</span>
        {tag && <span className="mute"> · {tag}</span>}
        {p.description && <span className="mute"> · {p.description}</span>}
      </div>
      <div className={`mono text-[12px] ${overdue ? 'text-alert' : 'mute'}`} style={{ width: 130 }}>
        {dueDate ? (overdue ? `scaduta il ${fmtDate(dueDate)}` : `scade il ${fmtDate(dueDate)}`) : '—'}
      </div>
      <div className="mono font-semibold text-right" style={{ width: 110 }}>€ {fmtEur(amount, true)}</div>
      <div className="relative flex items-center gap-1" style={{ width: 128 }}>
        <button className="btn btn-sm" disabled={pay.isPending} onClick={() => setOpen((v) => !v)}>
          <Icon name="check" size={10} /> Paga
        </button>
        {/* Stesso menu degli incassi: cestinare toglie il documento dal gestionale,
            non lo annulla su FattureInCloud. */}
        <ActionsMenu items={[{ key: 'trash', label: 'Cestina', icon: 'trash', danger: true, onClick: () => setTrash(true) }]} />
        {open && (
          <SettlePopover
            mode="paga"
            amount={amount}
            target={{ kind: 'PAYABLE_PAYMENT', id: paymentId }}
            counterparty={p.vendorName}
            side="DEBIT"
            pending={pay.isPending}
            onClose={() => setOpen(false)}
            onConfirm={(v) => { setOpen(false); pay.mutate({ source: 'PAYABLE', id: paymentId, amount, label: p.vendorName, ...v }); }}
          />
        )}
        {trash && (
          <TrashPopover
            title={p.number ?? p.vendorName}
            pending={ignoreDocs.isPending}
            onClose={() => setTrash(false)}
            onConfirm={(v) => { setTrash(false); ignoreDocs.mutate({ kind: 'payable', ids: [p.id], label, ...v }); }}
          />
        )}
      </div>
    </div>
  );
}
